/** Native spritesheet animation, sampled only from the runner's clock. No Pixi or DOM. */
import type { BundleSprite, BundleSpriteClip, BundleSpriteFrame, SpritePoints } from '@/schema/story'

import { authoredSide, mirrorFor, TURN_SQUASH } from './puppet'
import type { ActorFrame } from './puppetView'

export interface SpriteBinding { id: string; clip: BundleSpriteClip }

/** Pocket Pals clips drawn facing the camera or away; every other one is turned left. */
const POCKET_PALS_UNTURNED: ReadonlySet<string> = new Set(['back_idle', 'walk_back', 'walk_forward'])

/**
 * Stories pinned before the fix label the whole Pocket Pals pack `camera`, which is never
 * mirrored, so every character looked left. Read the art's real side instead: the lateral
 * walk faces left, the poses (idle, talk, carry…) are a three-quarter view turned left.
 * Mirrors backend/app/index/pocket_pals.py `clip_facing`.
 */
export function spriteClipFacing(sprite: BundleSprite, binding: SpriteBinding): string {
  const facing = binding.clip.facing
  if (!sprite.asset_uid.startsWith('char:pocket-pals/') || (facing && facing !== 'camera')) return facing
  if (POCKET_PALS_UNTURNED.has(binding.id)) return facing
  return binding.id === 'walk' && binding.clip.action === 'walk' ? 'left' : '3q_left'
}

const ALIASES: Readonly<Record<string, readonly string[]>> = {
  move: ['walk', 'run', 'fly', 'swim', 'hop', 'slither', 'crawl'],
  walk: ['move', 'run'], run: ['move', 'walk'], fly: ['move'], swim: ['move'],
  greet: ['wave'], wave: ['greet'], happy: ['cheer', 'dance', 'jump'],
  talk: ['speak'], reach: ['pick_up', 'wave'], pick_up: ['reach'], put_down: ['reach'],
  // Expressive story actions fall back to the nearest real pose a sheet has.
  celebrate: ['dance', 'jump', 'happy'], cheer: ['happy', 'jump'], surprised: ['happy'],
  angry: ['grumpy', 'stomp'], grumpy: ['angry'], stomp: ['angry'], sulk: ['sad'], sad: ['sulk'],
  carry: ['pickup_idle', 'hold'], hold: ['pickup_idle', 'carry'],
  walk_back: ['x:walk_back', 'walk'], walk_forward: ['walk'], back_idle: ['x:back_idle'],
}

export function resolveSpriteClip(sprite: BundleSprite | null | undefined, action: string): SpriteBinding | null {
  if (!sprite) return null
  for (const key of [action, ...(ALIASES[action] ?? []), 'idle', sprite.default_clip]) {
    const id = sprite.actions[key] ?? (sprite.clips[key] ? key : Object.keys(sprite.clips).find((id) => sprite.clips[id]?.action === key))
    const clip = id ? sprite.clips[id] : undefined
    if (id && clip?.frames.length) return { id, clip }
  }
  const entry = Object.entries(sprite.clips).find(([, c]) => c.frames.length > 0)
  return entry ? { id: entry[0], clip: entry[1] } : null
}

const clocks = new WeakMap<BundleSpriteClip, { ends: number[]; duration: number }>()

/** Variable frame durations use half-open intervals; one-shots hold the final frame. */
export function spriteFrameAt(clip: BundleSpriteClip, timeMs: number): { index: number; frame: BundleSpriteFrame } {
  let clock = clocks.get(clip)
  if (!clock) {
    let duration = 0
    const ends = clip.frames.map((frame) => {
      duration += Number.isFinite(frame.duration_ms) && frame.duration_ms > 0 ? frame.duration_ms : 1000 / Math.max(1, clip.fps)
      return duration
    })
    clock = { ends, duration }
    clocks.set(clip, clock)
  }
  const time = Number.isFinite(timeMs) ? Math.max(0, timeMs) : 0
  const at = clip.loop && clock.duration > 0 ? time % clock.duration : time
  let lo = 0
  let hi = clip.frames.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >>> 1
    if (at < clock.ends[mid]!) hi = mid
    else lo = mid + 1
  }
  return { index: lo, frame: clip.frames[lo]! }
}

function pointsAt(sprite: BundleSprite, clip: BundleSpriteClip, index: number): SpritePoints {
  return { ...sprite.points, ...clip.points, ...clip.frame_points?.[String(index)] }
}

/** True when the clip `action` binds carries its own hand point (on the clip or on its frames). */
export function spriteClipHasHands(sprite: BundleSprite | null | undefined, action: string): boolean {
  const clip = resolveSpriteClip(sprite, action)?.clip
  if (!clip) return false
  if (clip.points.hand) return true
  return Object.values(clip.frame_points ?? {}).some((p) => !!p?.hand)
}

/** True when the sheet has its own clip for `action` (no fallback to another pose). */
export function spriteHasAction(sprite: BundleSprite | null | undefined, action: string): boolean {
  if (!sprite) return false
  const id = sprite.actions[action] ?? (sprite.clips[action] ? action : undefined)
  return !!id && (sprite.clips[id]?.frames.length ?? 0) > 0
}

/** Rest height uses the first frame; moving head/feet annotations never rescale the body mid-clip. */
export function spriteBodyHeight(sprite: BundleSprite | null | undefined, action: string): number {
  const binding = resolveSpriteClip(sprite, action)
  if (!sprite || !binding) return 1
  const frame = binding.clip.frames[0]!
  const p = pointsAt(sprite, binding.clip, 0)
  const height = ((p.feet?.[1] ?? 100) - (p.head_top?.[1] ?? 0)) * frame.h / 100
  return Number.isFinite(height) && height >= Math.max(1, frame.h * 0.05) ? height : frame.h
}

export function spriteAspect(sprite: BundleSprite | null | undefined): number {
  if (!sprite) return 0.6
  let width = 0
  for (const [id, clip] of Object.entries(sprite.clips)) {
    const height = spriteBodyHeight(sprite, id)
    for (const f of clip.frames) width = Math.max(width, f.w / height)
  }
  return width > 0 ? Math.min(3, Math.max(0.2, width)) : 0.6
}

export function spriteAssetIds(sprite: BundleSprite | null | undefined): string[] {
  const ids = new Set<string>()
  for (const clip of Object.values(sprite?.clips ?? {})) for (const frame of clip.frames) ids.add(frame.asset)
  return [...ids]
}

export interface SpriteTransform {
  binding: SpriteBinding
  index: number
  frame: BundleSpriteFrame
  points: SpritePoints
  feet: [number, number]
  mirror: boolean
  scaleX: number
  scaleY: number
}

export function spriteTransformAt(sprite: BundleSprite | null | undefined, input: ActorFrame): SpriteTransform | null {
  const binding = resolveSpriteClip(sprite, input.clip)
  if (!sprite || !binding) return null
  const clip = binding.clip
  const hold = clip.loop && Number.isFinite(input.holdWallMs) ? Math.max(0, input.holdWallMs ?? 0) : 0
  const idle = clip.loop && clip.action === 'idle' && Number.isFinite(input.idleClockMs) ? input.idleClockMs! : input.clipTimeMs
  const { index, frame } = spriteFrameAt(clip, idle + hold)
  const points = pointsAt(sprite, clip, index)
  const facing = spriteClipFacing(sprite, binding)
  const mirror = mirrorFor(input.facing, { facing, mirror: clip.mirrored })
  const scale = Number.isFinite(input.scale) && input.scale > 0 ? input.scale : 1
  const turn = authoredSide(facing) && Number.isFinite(input.turn) ? input.turn! : 1
  // `liveliness.ts` squash / stretch about the feet: the sockets (a held prop) follow it. A free
  // move's twirl turns the body round through a negative x scale (`motion.ts`).
  const rawSx = Number.isFinite(input.squashX) ? input.squashX! : 1
  const sx = Math.abs(rawSx) < 1e-3 ? (rawSx < 0 ? -1e-3 : 1e-3) : rawSx
  const sy = Number.isFinite(input.squashY) && input.squashY! > 0 ? input.squashY! : 1
  return {
    binding, index, frame, points, feet: points.feet ?? [50, 100], mirror,
    scaleX: scale * (mirror ? -1 : 1) * turn * sx,
    scaleY: scale * (1 - TURN_SQUASH * (1 - Math.abs(turn))) * sy,
  }
}

/** How far down from the top of the head to the feet a sprite's eyes are, when it marks no `head`. */
export const HEAD_EYES = 0.2

/** Both conventional story sockets and any named annotation follow the selected sprite frame. */
export function spriteSocketAt(xf: SpriteTransform | null, name: string): { x: number; y: number; angle: number } | null {
  if (!xf) return null
  const aliases: Record<string, readonly string[]> = {
    hand: ['hand', 'right_hand', 'mouth'], other_hand: ['other_hand', 'hand2', 'left_hand', 'hand', 'mouth'],
    head: ['head', 'head_top'], top: ['top', 'head_top'], back: ['back', 'body', 'torso'],
  }
  let p = (aliases[name] ?? [name]).map((key) => xf.points[key]).find(Boolean)
  const top = xf.points.head_top
  if (name === 'head' && !xf.points.head && top) {
    // no head point: the eyes, a fifth of the way down from the top of the head — goggles go over
    // them, not on the crown (a hat goes to `top`)
    p = [top[0] + (xf.feet[0] - top[0]) * HEAD_EYES, top[1] + (xf.feet[1] - top[1]) * HEAD_EYES]
  }
  if (!p) return null
  return {
    x: ((p[0] - xf.feet[0]) / 100) * xf.frame.w * xf.scaleX || 0,
    y: ((p[1] - xf.feet[1]) / 100) * xf.frame.h * xf.scaleY || 0,
    angle: 0,
  }
}
